<template>
    <div class="flex w-full self-stretch inline-flex flex-col justify-start items-start">
        <div data-qty-btn="true" data-style="Multiple Btns" class="w-full p-4  inline-flex justify-start items-center gap-6">
            <div class="flex-1 flex justify-start items-center gap-2">
                <div class="flex-1 justify-center text-white text-xl font-bold font-['Roboto'] leading-6 tracking-tight">Profiles</div>
            </div>
            <button v-if="can_edit_business_brand_profiles" @click="showNewProfile" data-fill-color="Green" data-fill="Yes" data-show-copy="true" data-show-icon-l="true" data-show-icon-r="false" data-type="CTA" class="h-11 px-4 py-2 bg-green-500 rounded-md flex justify-center items-center gap-2 overflow-hidden">
                <div class="w-6 h-6 relative">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M19 13H13V19H11V13H5V11H11V5H13V11H19V13Z" fill="white"/>
                    </svg>
                </div>
                <div class="justify-center text-white text-sm font-medium font-['Roboto'] uppercase leading-4 tracking-widest">New Profile</div>
            </button>
        </div>
        
        <div v-if="loading" class="flex w-[calc(100%_-_100px)] justify-center items-center">
            <block-loading />
        </div>
        <div v-else-if="branding && branding.length" class="w-full h-screen overflow-y-auto pb-[200px]">
            <EachProfile
                v-for="(profile, index) in branding"
                :key="index"
                :brandingSize="branding.length"
                :branding="profile"
                :businessName="profile.name"
                :merchantWebsiteUrl="profile.domain"
                :googleAnalyticsTrackingId="profile.googleAnalyticsKey"
                :refetchAllLocationsKey="refetchAllLocationsKey"
                @showEditProfile="showEditProfile(profile)"
                @showManage="showManage($event)"
                @removed="handleProfileRemoved"
            />
        </div>
        <div v-else class="w-full h-screen overflow-y-auto px-[16px] py-[8px]">No profiles found</div>

        <NewProfile v-if="newProfilePop" @close="newProfilePop=false" @created="handleProfileCreated" />
        <EditProfile v-if="editProfilePop" :branding="selectedProfile" :businessName="selectedProfile?.name" :merchantWebsiteUrl="selectedProfile?.domain" :googleAnalyticsTrackingId="selectedProfile?.googleAnalyticsKey" @close="editProfilePop=false" @updated="handleProfileUpdated" />
        <Manage v-if="managePop" :profile="selectedProfileForManage" @close="handleManageClose" />
    </div>
</template>
<script lang="ts">
    import {defineComponent, ref} from "vue"
    import { mapGetters, mapActions } from "vuex";
    import Helpers from '@/helpers';
    import { useSnackbar } from "vue3-snackbar";
    import BrandingProfileApis from '@/controllers/BrandingProfileApis';
    import EachProfile from './EachProfile.vue'
    import NewProfile from './NewProfile.vue'
    import EditProfile from './EditProfile.vue'
    import Manage from './Manage.vue'

    interface BrandingProfile {
        id?: number;
        name: string;
        domain: string;
        googleAnalyticsKey: string;
        logo: string;
        favicon: string;
        primaryColor: string;
        secondaryColor: string;
        cashRounding?: boolean;
    }
    
    export default defineComponent({
        components: {
            EachProfile,
            NewProfile,
            EditProfile,
            Manage
        },
        setup() {
            const snackbar = useSnackbar();
            const newProfilePop = ref(false);
            const editProfilePop = ref(false);
            const managePop = ref(false);
            const loading = ref(false);
            const branding = ref<BrandingProfile[]>([]);
            const selectedProfile = ref<BrandingProfile | null>(null);
            const selectedProfileForManage = ref<BrandingProfile | null>(null);
            const refetchAllLocationsKey = ref(0);

            return {
                snackbar,
                newProfilePop,
                editProfilePop,
                managePop,
                loading,
                branding,
                selectedProfile,
                selectedProfileForManage,
                refetchAllLocationsKey
            };
        },
        computed: {
            can_edit_business_brand_profiles() {
                return Helpers.getMyPermission('can_edit_business_brand_profiles')
            },
            ...mapGetters({
                va_activeBusiness: 'auth/activeBusiness',
                vg_selectedLocation: 'location/selected',
            }),
        },
        async mounted() {
            await this.fetchBusinessBranding();
        },
        methods: {
            /**
             * Fetch business branding profiles (single API returns array of profiles with name, domain, googleAnalyticsKey, logo, favicon, primaryColor, secondaryColor)
             * @param options.skipLoading - if true, do not set loading state (e.g. when refetching after manage close)
             */
            async fetchBusinessBranding(options?: { skipLoading?: boolean }) {
                if (!options?.skipLoading) this.loading = true;
                try {
                    const res = await BrandingProfileApis.fetchBusinessBrandingProfile();
                    if (res?.status === 200 && Array.isArray(res?.data?.payload)) {
                        this.branding = res.data.payload;
                    } else {
                        this.branding = [];
                    }
                } catch (error) {
                    this.branding = [];
                } finally {
                    if (!options?.skipLoading) this.loading = false;
                }
            },
            showNewProfile(){
                this.newProfilePop = true
            },
            showEditProfile(profile){
                this.selectedProfile = profile || null;
                this.editProfilePop = true
            },
            showManage(profile?: BrandingProfile){
                this.selectedProfileForManage = profile || null;
                this.managePop = true
            },
            async handleProfileUpdated(){
                await this.fetchBusinessBranding();
                await this.va_setAllLocationsAndSelectedLocation(this.vg_selectedLocation.id);
            },
            async handleProfileCreated(){
                await this.fetchBusinessBranding();
            },
            async handleProfileRemoved(){
                await this.fetchBusinessBranding();
                await this.va_setAllLocationsAndSelectedLocation(this.vg_selectedLocation.id);
            },
            async handleManageClose(payload?: { reload?: boolean }) {
                this.managePop = false;
                if (payload?.reload) {
                    this.refetchAllLocationsKey++;
                    await this.fetchBusinessBranding({ skipLoading: true });
                    await this.va_setAllLocationsAndSelectedLocation(this.vg_selectedLocation.id);
                }
            },
            ...mapActions({
                va_setAllLocationsAndSelectedLocation: 'location/setAllLocationsAndSelectedLocation',
            })
        }
    })
</script>